import { onTrack } from './lib/telemetry.ts';

/**
 * PostHog, loaded lazily and only when a project key is baked in at build time (VITE_POSTHOG_KEY).
 * Cookieless (localStorage persistence), no autocapture, no session recording, no surveys: only the
 * named events from telemetry.ts plus a page view and page leave. Events fired before the SDK is
 * ready are queued and flushed. Never receives a raw email address, only its domain.
 */
type Props = Record<string, string | number | boolean | undefined>;
type Sink = { capture: (name: string, props?: Props) => unknown };

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';
const environment = (import.meta.env.VITE_ENVIRONMENT as string | undefined) || (import.meta.env.DEV ? 'local' : 'unknown');

export function mountAnalytics(): void {
  if (!key) return;
  const queue: [string, Props | undefined][] = [];
  let ph: Sink | null = null;
  onTrack((name, props) => { if (ph) ph.capture(name, props); else queue.push([name, props]); });

  const start = async () => {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: host,
      autocapture: false,
      capture_pageview: false, // captured below, after the environment super property is registered
      capture_pageleave: true,
      disable_session_recording: true,
      disable_surveys: true,
      disable_external_dependency_loading: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      persistence: 'localStorage',
      person_profiles: 'identified_only',
      loaded: (p) => {
        p.register({ environment });
        p.capture('$pageview');
        ph = p;
        for (const [n, pr] of queue) p.capture(n, pr);
        queue.length = 0;
      },
    });
  };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(() => void start(), { timeout: 3000 });
  else window.setTimeout(() => void start(), 1500);
}
